<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useThemeStore } from '@/stores/theme';

const theme = useThemeStore();
const container = ref<HTMLElement | null>(null);
const width = ref(0);
const height = ref(0);
const seed = Math.floor(Math.random() * 100_000);
let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
    if (!container.value) return;
    resizeObserver = new ResizeObserver((entries) => {
        const rect = entries[0]?.contentRect;
        if (!rect) return;
        width.value = rect.width;
        height.value = rect.height;
    });
    resizeObserver.observe(container.value);
});

onBeforeUnmount(() => {
    resizeObserver?.disconnect();
});

const glyphs = computed(() => {
    const bg = theme.currentConfig.background;
    if (!bg.glyphs.length || width.value < 10 || height.value < 10) return [];

    const count = Math.max(20, Math.min(160, Math.floor((width.value * height.value) / 8000)));
    const rng = mulberry32(seed + Math.floor(width.value) * 31 + Math.floor(height.value) * 17 + theme.mode.length);
    const baseColor = hexToRgb(bg.color);

    return Array.from({ length: count }, () => {
        const size = bg.sizeMin + rng() * (bg.sizeMax - bg.sizeMin);
        const alpha = bg.alphas[Math.floor(rng() * bg.alphas.length)] ?? 24;
        return {
            glyph: bg.glyphs[Math.floor(rng() * bg.glyphs.length)] ?? bg.glyphs[0],
            style: {
                left: `${rng() * Math.max(1, width.value - size)}px`,
                top: `${rng() * Math.max(1, height.value - size)}px`,
                fontSize: `${size}px`,
                color: `rgba(${baseColor.r}, ${baseColor.g}, ${baseColor.b}, ${(alpha / 255).toFixed(3)})`,
                opacity: String(0.6 + rng() * 0.4)
            }
        };
    });
});

function mulberry32(a: number) {
    return function next() {
        let t = (a += 0x6D2B79F5);
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function hexToRgb(hex: string) {
    const m = hex.replace('#', '');
    return {
        r: parseInt(m.slice(0, 2), 16),
        g: parseInt(m.slice(2, 4), 16),
        b: parseInt(m.slice(4, 6), 16)
    };
}
</script>

<template>
    <div ref="container" class="brand-glyph-field" aria-hidden="true">
        <span
            v-for="(item, index) in glyphs"
            :key="index"
            class="brand-glyph-field__glyph"
            :style="item.style"
        >
            {{ item.glyph }}
        </span>
    </div>
</template>

<style scoped>
.brand-glyph-field {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
    user-select: none;
}
.brand-glyph-field__glyph {
    position: absolute;
    line-height: 1;
}
</style>